<template>
	<div>
		<!-- Displayed value -->
		<form-field-readonly
			@click="openModal"
			@remove:clicked="onUnselect"
			:hasRemove="computedValue.type ? true : false"
		>
			<div class="xs-12">
				<span v-if="computedValue.type" class="c1">{{ computedValue.type }}</span>
				<span
					v-if="computedValue.value"
					class="ml1 c6 lh125"
				>{{ computedValue.value }}</span>
				<span v-else>
					<slot name="empty">---</slot>
				</span>
			</div>
		</form-field-readonly>

		<!-- Modal -->
		<core-modal :name="instanceKey">
			<div class="p4">
				<!-- Add new identity form -->
				<template v-if="showCreateForm">
					<entity-identity-create
						:entityId="entityId"
						:entityName="entityName"
						:createOptions="createOptions"
						@identity:created="afterCreateUpdate"
						@cancel="onToggleCreateIdentity"
					/>
				</template>

				<template v-else-if="showUpdateForm">
					<entity-identity-update
						:entityId="entityId"
						:entityName="entityName"
						:value="computedValue"
						@identity:updated="afterCreateUpdate"
						@cancel="onToggleUpdateIdentity"
					/>
				</template>

				<template v-else>
					<div class="xs-12 fw7 fs5 lh125 mb2">{{ $t("select_identity") }}</div>
					<!-- Entity name and crete new -->
					<div class="xs-12 flex wrap mb4">
						<!-- Headline -->
						<div class="xs-8 lh125">
							<span class="fw7">{{ entityName }}</span>
							{{ $t("has_these_identities") }}:
						</div>
						<!-- Button -->
						<div class="flex xs-4 xs-right">
							<j-button
								type="tertiary"
								@click="onToggleCreateIdentity"
							>{{ $t("add_new_identity") }}</j-button>
						</div>
					</div>

					<!-- Selection -->
					<div v-if="computedList && computedList.length">
						<div
							v-for="item in computedList"
							:key="item.id"
							class="r2 p2 mb2 cu2 bg5"
							:class="[
								{ 'bc2 b2': item.isSelected },
								{ 'bc7 b1': !item.isSelected }
							]"
							@click="onSelect($event, item.id)"
						>
							<!-- Type -->
							<div class="xs-12 flex wrap lh125 c1 fw7 mb2">
								<div class="xs-8">{{ item.type }}</div>
								<div class="flex xs-4 xs-right" v-if="item.isSuggested">
									<j-tag size="small" variant="warning" label="Suggested" />
								</div>
							</div>

							<div class="flex xs-12 xs-between">
								<!-- Value -->
								<div class="c1 lh125 mb2">{{ item.value }}</div>

								<!-- Edit and Delete Identity Buttons -->
								<!-- data-to-ignore is used on the container click handler, to detect if the click is on this button and discard the event -->
								<div>
									<j-button
										:data-to-ignore="true"
										type="tertiary"
										@click="onToggleUpdateIdentity"
										size="xsmall"
									>{{ $t("edit") }}</j-button>
									<j-button
										class="ml2"
										:data-to-ignore="true"
										type="error2"
										@click="onRemoveIdentity(item.id)"
										size="xsmall"
										:isDisabled="isDeleting"
									>{{ $t("remove") }}</j-button>
								</div>
							</div>
						</div>
					</div>

					<!-- No data -->
					<div
						v-else
						class="xs-12 bg5 r2 b1 bc7 p4 ta2 fs3 c6 fw7 mb4"
					>{{ $t("errors.no_data") }}</div>
				</template>
			</div>
		</core-modal>
	</div>
</template>

<script>
import EntityIdentityCreate from "./EntityIdentityCreate.vue"
import EntityIdentityUpdate from "./EntityIdentityUpdate.vue"

import CoreModal from "@/modules/core/components/CoreModal"
import JButton from "@/components/JButton"
import FormFieldReadonly from "../FormFieldReadonly.vue"
import JTag from "@/components/JTag"
import { getFieldData, shouldSuggest } from "../utils/index.js"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		EntityIdentityCreate,
		EntityIdentityUpdate,
		CoreModal,
		JButton,
		FormFieldReadonly,
		JTag
	},

	props: {
		/**
		 * Value represents `id`
		 */
		value: {
			type: String
		},

		/**
		 * Field from schema
		 * It's an object that inlcudes numerous properties, read docs for more details
		 * We compute some values based on those properties
		 */
		field: {
			type: Object,
			default: () => ({})
		},

		/**
		 * Options for filtering suggested facts
		 */
		suggest: {
			type: Object
		},

		/**
		 * Instructions for creation of a new fact
		 * What properties it can included is in docs
		 */
		createOptions: {
			type: Object
		}
	},

	data() {
		return {
			instanceKey: Math.random().toString(), // get unique key fro modal window

			showCreateForm: false,
			showUpdateForm: false,
			isDeleting: false,

			// Form for adding new identity
			isAdding: false,
			isAddingLoading: false,
			form: {
				type: "",
				value: ""
			}
		}
	},

	computed: {
		/**
		 * Entity object that we get from API
		 * We get some other properties from this object that are crucial for some operations
		 * e.g. entityId, entityName, entityType via __typename
		 * thus it has to contain properties: `__typename`, `id` `name`
		 */
		entity() {
			const entity = getFieldData("entity", this.field, this)
			return entity
		},

		/**
		 * Based on this we do API calls
		 */
		entityId() {
			return this.entity?.id
		},

		/**
		 * Based on this we do API calls
		 */
		entityType() {
			return this.entity?.__typename
		},

		/**
		 * When we need to display name of an entity
		 */
		entityName() {
			return this.entity?.name
		},

		/**
		 * Will return data based on relation field
		 */
		cache() {
			return getFieldData("relation", this.field, this)
		},

		/**
		 * What user can select from, displayed in UI
		 */
		computedList() {
			return this.cache
				?.map(item => ({
					...item,
					isSelected: item.id === this.value,
					isSuggested: shouldSuggest(item, this.suggest)
				}))
				.sort((a, b) => b.isSuggested - a.isSuggested) // sort based on suggested first
		},

		/**
		 * So that we do not display `id` but proper value
		 */
		computedValue() {
			const identity = this?.cache?.find(item => item?.id === this.value)
			if (!identity) return {}
			return identity
		}
	},

	methods: {
		// When user wants to open a picker modal,
		// check if if we actually have `entity` to pick data for
		// If not, show message and don't show modal
		openModal() {
			if (!this.entity) {
				this.$store.dispatch("addMessage", {
					delay: 1500,
					message: {
						type: "neutral",
						text: "You have to select entity first"
					}
				})
				return
			}

			this.showCreateForm = false
			this.showUpdateForm = false

			this.$modal.show(this.instanceKey)
		},

		onToggleCreateIdentity() {
			this.showCreateForm = !this.showCreateForm
		},

		onToggleUpdateIdentity() {
			this.showUpdateForm = !this.showUpdateForm
		},

		// When user selects an identity
		onSelect(event, id) {
			if (event?.target?.dataset?.toIgnore) return
			this.$modal.hide(this.instanceKey)
			this.$emit("input", id)
		},

		// When user unselects
		onUnselect() {
			this.$emit("input", "")
			this.$modal.hide(this.instanceKey)
		},

		async afterCreateUpdate(id) {
			this.showCreateForm = false
			this.showUpdateForm = false

			// Update all entities in store by refetching the case graph
			await this.$store.dispatch("caseManagement/fetchCase", {
				caseId: this.$store.state.caseManagement.caseId
			})

			// Select identity automatically
			// Order is important, we do it after we update store
			this.onSelect({}, id)
		},

		async onRemoveIdentity(id) {
			const query = gql`
				mutation removeIdentity($entityId: String!, $id: String!) {
					removeIdentity(entityId: $entityId, id: $id) {
						errors
						status
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					entityId: this.entityId,
					id
				}
			})

			if (res?.data?.removeIdentity?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.removeIdentity?.errors?.[0] || this.$t("errors.general")
				)
				this.isLoading = false
				return
			}

			// Success
			this.isLoading = false

			// Update all entities in store by refetching the case graph
			await this.$store.dispatch("caseManagement/fetchCase", {
				caseId: this.$store.state.caseManagement.caseId
			})
		}
	}
}
</script>
